import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext.js';
import NotificationProvider from './components/ui/NotificationProvider.jsx';
import BellIcon from './components/ui/BellIcon';
import AsistenteIA from './components/AsistenteIA/AsistenteIA.jsx';

// Componente de carga para usar con React.lazy
const LoadingFallback = () => (
  <div className="h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#4677c2]"></div>
  </div>
);

// Helper para manejar errores de carga de chunks (versiones cacheadas)
const lazyWithRetry = (componentImport, name) => {
  return React.lazy(() => 
    componentImport().catch(err => {
      // Si es un error de chunk, recargar la página para obtener la versión actualizada
      if (err.name === 'ChunkLoadError' || err.message?.includes('Loading chunk')) {
        console.warn(`ChunkLoadError en ${name}, recargando página...`);
        // Evitar bucle infinito: solo recargar si no lo hemos hecho recientemente
        const lastReload = sessionStorage.getItem('lastChunkReload');
        const now = Date.now();
        if (!lastReload || now - parseInt(lastReload) > 10000) {
          sessionStorage.setItem('lastChunkReload', now.toString());
          window.location.reload();
        }
      }
      console.error(`Error loading ${name}:`, err);
      return { 
        default: () => (
          <div className="p-4 text-center">
            <p className="text-red-600 mb-4">Error al cargar {name}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-blue-600 text-white px-4 py-2 rounded"
            >
              Recargar página
            </button>
          </div>
        )
      };
    })
  );
};

// Importaciones lazy con manejo de errores y recarga automática
const LoginScreen = lazyWithRetry(() => import('./pages/LoginScreen'), 'LoginScreen');
const HomeScreen = lazyWithRetry(() => import('./pages/HomeScreen'), 'HomeScreen');
const Formularios = lazyWithRetry(() => import('./pages/Formularios'), 'Formularios');
const Entradas = lazyWithRetry(() => import('./pages/Entradas'), 'Entradas');
const Editar = lazyWithRetry(() => import('./pages/Editar'), 'Editar');
const Select = lazyWithRetry(() => import('./pages/Select'), 'Select');
const Cuadrantes = lazyWithRetry(() => import('./pages/CuadrantesScreen'), 'CuadrantesScreen');
const SolicitudesCambio = lazyWithRetry(() => import('./pages/SolicitudesCambioScreen'), 'SolicitudesCambioScreen');
const Notifications = lazyWithRetry(() => import('./pages/NotificationScreen.jsx'), 'NotificationScreen');
const ControlRondas = lazyWithRetry(() => import('./pages/ControlRondasScreen'), 'ControlRondasScreen');
const IniciarRonda = lazyWithRetry(() => import('./pages/IniciarRondaScreen'), 'IniciarRondaScreen');
const RondaProgreso = lazyWithRetry(() => import('./pages/RondaProgresoScreen'), 'RondaProgresoScreen');
const HistorialRondas = lazyWithRetry(() => import('./pages/HistorialRondasScreen'), 'HistorialRondasScreen');
const BuzonAnonimo = lazyWithRetry(() => import('./pages/BuzonAnonimoScreen'), 'BuzonAnonimoScreen');
const PortalEmpleado = lazyWithRetry(() => import('./pages/PortalEmpleadoScreen'), 'PortalEmpleadoScreen');
const PortalEmpleadoHub = lazyWithRetry(() => import('./pages/PortalEmpleadoHubScreen'), 'PortalEmpleadoHubScreen');
const Solicitudes = lazyWithRetry(() => import('./pages/SolicitudesScreen'), 'SolicitudesScreen');
const ComunicadoDetalle = lazyWithRetry(() => import('./pages/ComunicadoDetalleScreen'), 'ComunicadoDetalleScreen');
const SolicitudDetalle = lazyWithRetry(() => import('./pages/SolicitudDetalleScreen'), 'SolicitudDetalleScreen');
const Documentos = lazyWithRetry(() => import('./pages/DocumentosScreen'), 'DocumentosScreen');
const SeguimientoDatos = lazyWithRetry(() => import('./pages/SeguimientoDatosScreen'), 'SeguimientoDatosScreen');
const SeguimientoDatosDetalle = lazyWithRetry(() => import('./pages/SeguimientoDatosDetalleScreen'), 'SeguimientoDatosDetalleScreen');

// Componente Header que replica el comportamiento del Stack.Screen
const Header = ({ title, servicioId }) => {
  const navigate = useNavigate();

  return (
    <div className="bg-[#4677c2] p-4 flex items-center justify-between">
      <button 
        onClick={() => navigate(-1)}
        className="text-white hover:bg-[#4677c2]/80 p-2 rounded-full transition-colors"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <h1 className="text-white text-lg font-medium">{title || 'Página'}</h1>
      {servicioId ? (
        <BellIcon servicioId={servicioId} />
      ) : (
        <span className="w-10" />
      )}
    </div>
  );
};

// Wrapper para las rutas que necesitan header
const RouteWithHeader = ({ component: Component, title }) => {
  const location = useLocation();
  
  // Extraer servicioId de forma segura
  let servicioId = null;
  try {
    servicioId = location.state?.servicioId;
    // Verificar que servicioId es válido
    if (servicioId && typeof servicioId !== 'string') {
      console.warn('servicioId en state no es un string:', servicioId);
      servicioId = String(servicioId);
    }
  } catch (error) {
    console.error('Error al acceder a location.state:', error);
  }

  // Add safety check
  if (!Component) {
    console.error(`Component is undefined for title "${title}"`);
    return (
      <div className="h-screen flex flex-col">
        <Header title={title} servicioId={servicioId} />
        <div className="flex-1 overflow-auto bg-red-100 p-4">
          <p>Error: Componente no encontrado</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      <Header title={title} servicioId={servicioId} />
      <div className="flex-1 overflow-auto">
        <Suspense fallback={<LoadingFallback />}>
          <Component />
        </Suspense>
      </div>
    </div>
  );
};

// AppRoutes component para manejar las rutas
const AppRoutes = () => {
  return (
    <NotificationProvider>
      <Routes>
        <Route path="/" element={
          <Suspense fallback={<LoadingFallback />}>
            <LoginScreen />
          </Suspense>
        } />
        <Route path="/documentos" element={
          <Suspense fallback={<LoadingFallback />}>
            <HomeScreen />
          </Suspense>
        } />
        <Route 
          path="/formularios" 
          element={<RouteWithHeader component={Formularios} title="Reportes" />} 
        />
        <Route 
          path="/entradas" 
          element={<RouteWithHeader component={Entradas} title="Entradas" />} 
        />
        <Route 
          path="/editar" 
          element={<RouteWithHeader component={Editar} title="Editar" />} 
        />
        <Route 
          path="/ver" 
          element={<RouteWithHeader component={Select} title="Seleccionar" />} 
        />
        <Route 
          path="/notificaciones" 
          element={<RouteWithHeader component={Notifications} title="Notificaciones" />} 
        />
        <Route
          path="/cuadrantes"
          element={<RouteWithHeader component={Cuadrantes} title="Cuadrantes" />}
        />
        <Route
          path="/cambios-turno"
          element={<RouteWithHeader component={SolicitudesCambio} title="Cambios de Turno" />}
        />
        {/* Nuevas rutas para Control de Rondas */}
        <Route 
          path="/control-rondas" 
          element={<RouteWithHeader component={ControlRondas} title="Control de Rondas" />} 
        />
        <Route 
          path="/iniciar-ronda" 
          element={<RouteWithHeader component={IniciarRonda} title="Iniciar Ronda" />} 
        />
        <Route 
          path="/ronda-progreso" 
          element={<RouteWithHeader component={RondaProgreso} title="Ronda en Progreso" />} 
        />
        <Route 
          path="/historial-rondas" 
          element={<RouteWithHeader component={HistorialRondas} title="Historial de Rondas" />} 
        />
        {/* Ruta para el Buzón Anónimo */}
        <Route
          path="/buzon-anonimo"
          element={<RouteWithHeader component={BuzonAnonimo} title="Buzón Anónimo" />}
        />
        {/* Portal del Empleado: hub + Datos Personales + Solicitudes */}
        <Route
          path="/portal-empleado"
          element={<RouteWithHeader component={PortalEmpleadoHub} title="Portal del Empleado" />}
        />
        <Route
          path="/portal-empleado/datos"
          element={<RouteWithHeader component={PortalEmpleado} title="Gestión de Datos Personales" />}
        />
        <Route
          path="/portal-empleado/solicitudes"
          element={<RouteWithHeader component={Solicitudes} title="Gestión de Solicitudes" />}
        />
        <Route
          path="/portal-empleado/solicitudes/comunicado"
          element={<RouteWithHeader component={ComunicadoDetalle} title="Comunicado" />}
        />
        <Route
          path="/portal-empleado/solicitudes/detalle"
          element={<RouteWithHeader component={SolicitudDetalle} title="Solicitud" />}
        />
        <Route
          path="/portal-empleado/documentos"
          element={<RouteWithHeader component={Documentos} title="Documentos" />}
        />
        <Route
          path="/portal-empleado/seguimiento"
          element={<RouteWithHeader component={SeguimientoDatos} title="Seguimiento de Solicitudes" />}
        />
        <Route
          path="/portal-empleado/seguimiento/detalle"
          element={<RouteWithHeader component={SeguimientoDatosDetalle} title="Solicitud de cambio" />}
        />
        {/* Ruta para capturar cualquier URL no definida */}
        <Route path="*" element={
          <div className="h-screen flex items-center justify-center flex-col">
            <h1 className="text-2xl font-bold mb-4">Página no encontrada</h1>
            <button 
              onClick={() => window.history.back()}
              className="bg-blue-600 text-white px-4 py-2 rounded"
            >
              Volver
            </button>
          </div>
        } />
      </Routes>
    </NotificationProvider>
  );
};

const App = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="h-screen bg-gray-100">
          <React.Suspense fallback={<LoadingFallback />}>
            <AppRoutes />
          </React.Suspense>
          <AsistenteIA />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;